import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Legal disclaimer scroll — shown ONCE before the GardenGate warning on first visit.
 * Acceptance is persisted in localStorage so returning users don't re-see it.
 */
const STORAGE_KEY = "primordeia.legal.v1.accepted";

const LegalScroll = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const accepted = localStorage.getItem(STORAGE_KEY) === "true";
      setVisible(!accepted);
    } catch { setVisible(true); }
  }, []);

  const handleAccept = () => {
    try { localStorage.setItem(STORAGE_KEY, "true"); } catch { /* ignore */ }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[500] flex items-center justify-center p-4"
          style={{ background: "rgba(20, 12, 40, 0.78)", backdropFilter: "blur(6px)" }}
          data-testid="legal-scroll-overlay"
        >
          <motion.div
            initial={{ scale: 0.92, y: 24, rotate: -1 }}
            animate={{ scale: 1, y: 0, rotate: 0 }}
            transition={{ type: "spring", damping: 22, stiffness: 220 }}
            className="relative max-w-lg w-full"
            data-testid="legal-scroll-paper"
          >
            {/* Scroll handles */}
            <div className="absolute -top-3 left-0 right-0 h-6 rounded-full"
              style={{ background: "linear-gradient(180deg, #6B4226 0%, #3D2A0E 100%)", boxShadow: "0 2px 10px rgba(0,0,0,0.35)" }} />
            <div className="absolute -bottom-3 left-0 right-0 h-6 rounded-full"
              style={{ background: "linear-gradient(0deg, #6B4226 0%, #3D2A0E 100%)", boxShadow: "0 2px 10px rgba(0,0,0,0.35)" }} />

            {/* Scroll body */}
            <div className="px-7 py-9 relative"
              style={{
                background: "linear-gradient(180deg, #FBF1D8 0%, #F7E7C1 50%, #EDD6A0 100%)",
                boxShadow: "inset 0 0 30px rgba(123,72,30,0.18), 0 18px 60px rgba(0,0,0,0.45)",
                fontFamily: "'Cinzel', serif",
                color: "#3D2A0E",
              }}
            >
              <div className="text-center mb-4">
                <div className="text-[10px] tracking-[0.32em] uppercase opacity-70">Primordeia · Legal Disclaimer</div>
                <h2 className="text-xl font-bold mt-1" data-testid="legal-scroll-title">Decree of the Cottage</h2>
              </div>

              <p className="text-[12.5px] leading-relaxed italic"
                style={{ fontFamily: "'Nunito', serif", color: "#3D2A0E" }}
                data-testid="legal-scroll-body">
                This software contains 0% FDA-approved medical advice and 100% unconventional wisdom.
                We are not responsible for any sudden urges to quit your corporate job, align your chakras,
                or realize that the modern medical industrial complex is just three corporations in a
                trench coat. Use at your own risk. Or don't. If you use this tool, you acknowledge that the
                developer is a spiritual guide, not a pharmacist. We provide insights for the soul; if you're
                looking for clinical diagnoses, please seek a practitioner with more framed certificates and
                less intuition. Any enlightenment achieved is your own responsibility; any failure to
                transcend is likely a user-end hardware issue.
              </p>

              <div className="flex justify-center mt-6">
                <button onClick={handleAccept}
                  className="px-7 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest hover:scale-[1.03] transition-transform"
                  style={{
                    background: "linear-gradient(135deg, #4F378B 0%, #7B5EA7 100%)",
                    color: "#FFF",
                    boxShadow: "0 6px 18px rgba(79,55,139,0.4)",
                    fontFamily: "'Cinzel', serif",
                  }}
                  data-testid="legal-scroll-accept"
                >
                  I understand &mdash; proceed
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LegalScroll;
